/**
 * Падащ списък с предложения — по избор групиран (напр. по стокова група).
 *
 * Изваден от НовДокумент, за да не се дублира: търсенето на стока трябва
 * да изглежда еднакво навсякъде в приложението, а не различно на всеки
 * екран, откъдето се добавя стока.
 */
import { useEffect, useState } from 'react';

export default function Suggestions({
  items, render, onPick, footer = null, groupBy = null, activeId = null,
}) {
  const grupi = groupBy ? groupiraj(items, groupBy) : null;

  const red = (it) => {
    const aktiven = activeId != null && it.id === activeId;
    return (
      <div
        key={it.id}
        // Маркираният със стрелките ред се вижда винаги — списъкът скролва след него.
        ref={aktiven ? (el) => el?.scrollIntoView({ block: 'nearest' }) : undefined}
        onMouseDown={(e) => { e.preventDefault(); onPick(it); }}
        style={{
          padding: '8px 11px', cursor: 'pointer', color: 'var(--text)',
          background: aktiven ? 'var(--accent-soft)' : 'transparent',
          boxShadow: aktiven ? 'inset 3px 0 0 var(--accent)' : 'none',
        }}
        onMouseEnter={(e) => { if (!aktiven) e.currentTarget.style.background = 'var(--surface-2)'; }}
        onMouseLeave={(e) => { if (!aktiven) e.currentTarget.style.background = 'transparent'; }}
      >
        {render(it)}
      </div>
    );
  };

  return (
    <div style={{
      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 6, boxShadow: 'var(--shadow)', overflow: 'hidden',
      // Списъкът скролва, вместо да се реже: бърз код „УФР" хваща 40 стоки
      // и осмата не е по-важна от трийсетата.
      maxHeight: '60vh', overflowY: 'auto',
    }}
    >
      {grupi
        ? grupi.map(([ime, redove], i) => (
          <div key={ime} style={i > 0 ? { borderTop: '1px solid var(--border)' } : undefined}>
            {/*
              * Хедърът на групата трябва да се различава от редовете под
              * него с един поглед, не само с малко по-сив фон — иначе на
              * дълъг списък всичко се слива в едно. Затова е с цветен фон
              * и удебелен текст в цвета на маркирането, не просто "muted".
              */}
            <div style={{
              padding: '7px 11px', fontSize: 12, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.4,
              color: 'var(--accent)',
              background: 'color-mix(in srgb, var(--accent) 14%, var(--surface-2))',
              borderBottom: '1px solid var(--border)',
              position: 'sticky', top: 0, zIndex: 1,
            }}
            >
              {ime}
            </div>
            {redove.map(red)}
          </div>
        ))
        : items.map(red)}
      {footer && (
        <div className="muted" style={{
          padding: '6px 11px', fontSize: 11, borderTop: '1px solid var(--border)',
          position: 'sticky', bottom: 0, background: 'var(--surface)',
        }}
        >
          {footer}
        </div>
      )}
    </div>
  );
}

/**
 * Клавиатура за падащия списък (09.10.2026): стрелки нагоре/надолу местят
 * маркирания ред, Enter избира него — или първия, ако нищо не е
 * маркирано. Редът следва видимия списък, т.е. след групирането.
 *
 *   const keys = useSuggestKeys(items, onPick, groupBy, otvoren);
 *   <input onKeyDown={keys.onKeyDown} />  <Suggestions activeId={keys.activeId} … />
 *
 * onKeyDown връща true, ако клавишът е обработен.
 */
export function useSuggestKeys(items, onPick, groupBy = null, otvoren = true) {
  const [i, setI] = useState(-1);
  useEffect(() => { setI(-1); }, [items]);
  const red = groupBy ? groupiraj(items, groupBy).flatMap(([, r]) => r) : items;
  const onKeyDown = (e) => {
    if (!otvoren || red.length === 0) return false;
    if (e.key === 'ArrowDown') {
      e.preventDefault(); setI((x) => Math.min(x + 1, red.length - 1)); return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault(); setI((x) => Math.max(x - 1, 0)); return true;
    }
    if (e.key === 'Enter') {
      e.preventDefault(); onPick(red[i >= 0 && i < red.length ? i : 0]); setI(-1); return true;
    }
    return false;
  };
  return { activeId: red[i]?.id ?? null, onKeyDown };
}

/** Групира по реда, в който групите се срещат за пръв път — без пресортиране. */
export function groupiraj(items, groupBy) {
  const karta = new Map();
  for (const it of items) {
    const ime = groupBy(it) || 'Без категория';
    if (!karta.has(ime)) karta.set(ime, []);
    karta.get(ime).push(it);
  }
  return [...karta.entries()];
}
